import type { OnInit } from '@angular/core';
import {
  Component,
  ElementRef,
  inject,
  Input,
  ViewEncapsulation
} from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import type { ApiProvider, ScrollEvent } from '@helsana/lerb-finder';
import {
  AVMProduct,
  Language,
  LerbFinderComponent,
  LerbFinderMode,
  ProviderType,
  ScrollingClickEvent
} from '@helsana/lerb-finder';
import { distinctUntilChanged, tap } from 'rxjs';

declare global {
  interface Window {
    helsana: {
      closeLightboxModal: () => void;
    };
  }
}

export interface PIndicatorUser {
  localityName: string;
  insuranceProfile: any;
  insuranceConfig: any;
  doctor: Doctor;
}

export interface Doctor {
  avmArztId: string;
  avmNetzId: string;
  isGroupPractice: boolean;
  providerName: string;
  isBPFAvailable: boolean;
}

@UntilDestroy()
@Component({
  standalone: true,
  selector: 'lerb-component', // tslint:disable-line
  templateUrl: './lerb.component.html',
  styleUrls: ['./lerb.component.scss'],
  imports: [LerbFinderComponent],
  encapsulation: ViewEncapsulation.ShadowDom
})
export class LerbComponent implements OnInit {
  @Input() mode!: LerbFinderMode | undefined;
  @Input() language!: Language | undefined;
  @Input() product!: AVMProduct | undefined;
  @Input() host!: string | undefined;
  @Input() initialType!: string | undefined;
  @Input() isOverlayMode = true;

  private elRef = inject(ElementRef);
  private breakpointObserver = inject(BreakpointObserver);

  private offSetTop = 65;
  private readonly mobileBreakpointOverlay = '(max-width: 769px)';
  private readonly STORAGE_USER_KEY: string = 'p-indicator-user';
  private readonly OVERLAY_SELECTOR: string = '.gslide-media.gslide-inline';
  private readonly LIGHTBOX_SELCTOR: string =
    '#provider-search-lightbox.ginlined-content';

  readonly breakpoint$ = this.breakpointObserver
    .observe([this.mobileBreakpointOverlay])
    .pipe(
      tap((value) => (this.isMobile = value.matches)),
      distinctUntilChanged()
    );

  private offset: { x: number; y: number } = { x: 0, y: 0 };
  private isMobile = false;

  ngOnInit(): void {
    this.breakpoint$.pipe(untilDestroyed(this)).subscribe();
  }

  public getMode(): LerbFinderMode {
    return this.mode || LerbFinderMode.Provider;
  }

  public getLanguage(): Language {
    return this.language || Language.de;
  }

  public getProduct(): AVMProduct | undefined {
    return this.product;
  }

  public getHost() {
    return this.host || '';
  }

  public getInitialType() {
    return this.initialType || '';
  }

  public setScrollOffset(isSearchComponentDesroyed: boolean) {
    if (isSearchComponentDesroyed) {
      const [x, y] = this.getOffset(this.elRef.nativeElement);
      this.offset = {
        x,
        y: y < 0 ? y * -1 : y
      };
    }
  }

  /**
   * Only manipulate the scroll position if the overlay is visible.
   * @param event
   * @returns
   */
  public async handleScrollEvent({
    eventName,
    element
  }: ScrollEvent): Promise<void> {
    const overlay = this.isMobile
      ? this.getLightboxElement()
      : this.getOverlayElement();

    if (!overlay) return;

    const [pageOffsetX, pageOffsetY] = this.getPageOffset();
    if (eventName === ScrollingClickEvent.MobileOverlayBackClickedEvent) {
      const { x, y } = this.offset;
      overlay.scrollTo(x, y);
    } else {
      this.scrollTo(overlay, element);
    }

    this.scrollWindow(pageOffsetX, pageOffsetY);
  }

  public lerbFinderClose() {
    window?.helsana?.closeLightboxModal();
  }

  public getIsOverlayMode(): boolean {
    return this.isOverlayMode;
  }

  public getOffSetTop(): number {
    return this.offSetTop;
  }

  public doctorSelect(doctor: ApiProvider) {
    if (typeof doctor !== 'object' || !doctor.name) {
      return;
    }
    const pindicatorUser = this.readPIndicatorUserFromLocalStorage();
    const updatedPindicatorUser = this.mapApiProviderToLocalStorage(
      doctor,
      pindicatorUser
    );
    this.writePIndicatorUserToLocalStorage(updatedPindicatorUser);
    this.sendDoctorUpdatedEvent(updatedPindicatorUser);
    this.lerbFinderClose();
  }

  private scrollTo(overlayElement: HTMLElement, scrollElement: HTMLElement) {
    overlayElement.scrollTo({
      behavior: 'smooth',
      top: scrollElement.offsetTop || 0
    });
  }

  private sendDoctorUpdatedEvent(updatedPindicatorUser: PIndicatorUser) {
    document.dispatchEvent(
      new CustomEvent('doctorUpdated', {
        bubbles: true,
        detail: updatedPindicatorUser.doctor
      })
    );
  }

  private readPIndicatorUserFromLocalStorage() {
    let storageValue;
    try {
      storageValue = localStorage.getItem(this.STORAGE_USER_KEY);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log('Error reading doctor from local storage');
    }
    return storageValue ? JSON.parse(storageValue) : undefined;
  }

  private writePIndicatorUserToLocalStorage(pindicatorUser: PIndicatorUser) {
    try {
      localStorage.setItem(
        this.STORAGE_USER_KEY,
        JSON.stringify(pindicatorUser)
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log('Error writing doctor to local storage');
    }
  }

  private mapApiProviderToLocalStorage(
    apiProvider: ApiProvider,
    pindicatorUser: PIndicatorUser
  ): PIndicatorUser {
    return {
      ...pindicatorUser,
      doctor: {
        avmArztId: apiProvider.id,
        avmNetzId: apiProvider.avmnetzId,
        isGroupPractice: apiProvider.typ === ProviderType.AVM_GROUP,
        providerName: `${apiProvider.vorname || ''} ${
          apiProvider.name || ''
        }`.trim(),
        isBPFAvailable: apiProvider.products.some(
          (doctor) => doctor.avmdefId === AVMProduct.Flexmed
        )
      }
    };
  }

  private getOverlayElement(): HTMLElement | null {
    return document.querySelector(this.OVERLAY_SELECTOR);
  }

  private getLightboxElement(): HTMLElement | null {
    return document.querySelector(this.LIGHTBOX_SELCTOR);
  }

  private getPageOffset(): number[] {
    return [window.scrollX || 0, window.scrollY || 0];
  }

  private getOffset(element: HTMLElement): number[] {
    return [
      element.getBoundingClientRect().x || 0,
      element.getBoundingClientRect().y || 0
    ];
  }

  private getElementOffsetTop(element: HTMLElement, offset = 0) {
    const { top } = element.getBoundingClientRect();
    const bodyRectTop = document.body.getBoundingClientRect().top;
    return top - bodyRectTop - offset;
  }

  private scrollElementIntoView(
    element: HTMLElement,
    options: ScrollIntoViewOptions
  ) {
    element.scrollIntoView(options);
  }

  private scrollWindow(x: number, y: number) {
    window.scrollTo(x, y);
  }
}
